/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useState, useEffect, useContext } from 'react';
import Alert from 'react-s-alert';
import { Table, Progress } from 'reactstrap';
import moment from 'moment';
import Joyride, { STATUS } from 'react-joyride';

// context imports
import { ThemeContext } from '../../../contexts/ThemeContext';
import { AuthContext } from '../../../contexts/AuthContext';

// service imports
import DemoService from '../../../services/DemoService';

// local imports
import Message from '../../general/Message';
import ErrorBoundary from '../../general/ErrorBoundary';
import ClientNavbar from './ClientNavbar';
import SelectedProvider from './SelectedProvider';
import Loading from '../../../assets/UW_Horizontal_Logo_Full.png';
import FullPageLoader from '../../general/FullPageLoader';
import FindProviderImg from '../../../assets/active_support.svg';
import { counsellors, clientSubscriptions, clientSubscriptionPlans } from '../demoData';

const Subscriptions = props => {

    // contexts
    const { theme, fontWeight, colors } = useContext(ThemeContext);
    const { selectedTimeZone } = useContext(AuthContext);

    // states
    const [user, setUser] = useState({ firstName: "", lastName: "", email: "", selectedRole: '', roles: [], profileImage: "", demoTakenFor: [] });
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [availablePlans, setAvailablePlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState({});
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [planCancelled, setPlanCancelled] = useState({
        status: false,
        message: {
            msgBody: '',
            msgError: true
        }
    });
    const [tutorial, setTutorial] = useState({
        status: false,
        steps: [
            {
                content: <h5>Manage your subscription here.</h5>,
                placement: 'center',
                target: '#clientSubscriptionDiv',
            },
            {
                content: <div className="tooltipDiv">Select counsellor of your choice by pressing here.</div>,
                placement: 'right',
                target: '#chooseProviderBtn',
            },
            {
                content: <div className="tooltipDiv">Select subscription plan of your choice from here.</div>,
                placement: 'bottom',
                target: '#subscriptionPlanList',
            },
            {
                content: <div className="tooltipDiv">Once selected, process the payment by pressing here.</div>,
                placement: 'right',
                target: '#paymentBtn',
            },
            {
                content: <div className="tooltipDiv">View your current subscription details from here.</div>,
                placement: 'bottom',
                target: '#subscriptionDetailDiv',
            },
            {
                content: <div className="tooltipDiv">Update/Change your current subscription plan by pressing here.</div>,
                placement: 'left',
                target: '#updatePlanBtn',
            },
            {
                content: <div className="tooltipDiv">Cancel your current subscription plan by pressing here.</div>,
                placement: 'left',
                target: '#cancelPlanBtn',
            }
        ]
    });
    let helpers = null;

    // useEffects
    useEffect(() => {
        setIsLoading(true);
        DemoService.getUserData().then(data => {
            if (data.isAuthenticated) {
                if (data.user.roles[0] === "DemoClient") {
                    setUser(data.user);
                    if (!data.user.demoTakenFor.includes("ClientSubscriptions")) {
                        setTutorial({ ...tutorial, status: true });
                    }
                    try {
                        setSelectedProvider({ ...counsellors[0], isTrue: true, name: `${counsellors[0].title ? counsellors[0].title : ''} ${counsellors[0].firstName} ${counsellors[0].lastName}` });
                        try {
                            setAvailablePlans(clientSubscriptionPlans);
                            try {
                                setCurrentSubscription(clientSubscriptions[0]);
                                setIsLoading(false);
                            } catch (err) {
                                setIsLoading(false);
                                console.log("err", err);
                                Alert.error("Some error occured, please try again later or report support team.", {
                                    position: 'top-right',
                                    effect: 'slide'
                                });
                                // GeneralService.logError('at BookSession useEffect2_1', err.stack);
                            }
                        } catch (err) {
                            setIsLoading(false);
                            console.log("err2", err);
                            Alert.error("Some error occured, please try again later or report support team.", {
                                position: 'top-right',
                                effect: 'slide'
                            });
                            // GeneralService.logError('at BookSession useEffect2_1', err.stack);
                        }
                    } catch (err) {
                        setIsLoading(false);
                        Alert.error("Some error occured, please try again later or report support team.", {
                            position: 'top-right',
                            effect: 'slide'
                        });
                        // GeneralService.logError('at Client BookSession useEffect2_1', 'Error in try catch block', err.stack);
                    }
                } else {
                    props.history.replace('PagePath');
                }
            } else {
                props.history.replace('/demo/signin');
            }
        })
    }, []);

    // useEffect(()=>console.log(currentSubscription,currentSubscription, client))

    // handle functions
    const handlePlanSelect = e => {
        if (selectedPlan.id) {
            try {
                props.history.push({
                    pathname: "/demo/client/makesubscriptionpayment",
                    state: {
                        selectedProvider: selectedProvider,
                        selectedPlan: selectedPlan
                    }
                });
            } catch (err) {
                Alert.error("Some error occured, please try again later or report support team.", {
                    position: 'top-right',
                    effect: 'slide'
                });
                // GeneralService.logError('at BookSession handleSubmit', err.stack);
            }
        }
    }

    const handleCancelPlan = e => {
        props.history.push({
            pathname: "/demo/client/cancelsubscription",
            state: {
                selectedProvider: selectedProvider,
                currentSubscription: { ...currentSubscription, clientSubscriptionPlan: clientSubscriptionPlans[0] }
            }
        });
    }

    const handleJoyrideCallback = (data) => {
        const { status, type } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status)) {
            if (!user.demoTakenFor.includes("ClientSubscriptions")) {
                DemoService.markVisited("ClientSubscriptions");
                setUser({ ...user, demoTakenFor: [...user.demoTakenFor, "ClientSubscriptions"] });
            }
            setTutorial({ ...tutorial, status: false });
        }
    };

    const getHelpers = (paramHelpers) => {
        helpers = paramHelpers;
    };

    // function's return
    return (
        <div id="clientSubscriptionDiv">
            <ErrorBoundary errorAt="At Client Subscriptions Joyride">
                <Joyride
                    callback={handleJoyrideCallback}
                    continuous={true}
                    getHelpers={getHelpers}
                    run={false && tutorial.status}
                    scrollToFirstStep={true}
                    showProgress={false}
                    showSkipButton={true}
                    steps={tutorial.steps}
                    locale={{ last: 'Finish' }}
                    styles={{
                        options: {
                            primaryColor: "rgb(56, 78, 115)",
                            zIndex: 10000,
                        },
                    }}
                />
            </ErrorBoundary>
            <ClientNavbar history={props.history} showTutorial={() => setTutorial({ ...tutorial, status: true })} />
            {
                isLoading ?
                    <img className="mx-auto d-block" height="auto" width="40%" src={Loading} alt="Loading..." />
                    :
                    <div className="p-2">
                        {
                            selectedProvider ?
                                currentSubscription && currentSubscription.isActive ?
                                    <div>
                                        {/* Page Heading */}
                                        <div>
                                            <h1 id="subscription" style={theme.headingBold}>Subscription</h1>
                                            <hr style={{ borderWidth: "2px" }} />
                                        </div>
                                        {planCancelled.status ? <Message message={planCancelled.message} /> : null}
                                        <div className="d-flex">
                                            <div id="subscription-plan-details" style={{ fontWeight: "500" }}>Current Subscription Plan Details</div>
                                            {
                                                currentSubscription.isCancelled ? null
                                                    :
                                                    [<div key="1" id="updatePlanBtn" className="ml-auto" style={{ color: colors.primary, cursor: "pointer" }} onClick={() => props.history.push('/demo/client/updateSubscription')}>Change Plan<i className="fas fa-external-link-alt ml-1"></i></div>,
                                                    <div key="2" id="cancelPlanBtn" className="ml-3" style={{ color: colors.danger, cursor: "pointer" }} onClick={() => handleCancelPlan()}>Cancel Plan</div>]
                                            }
                                        </div>
                                        <hr />
                                        <ErrorBoundary errorAt="At Client Subsciptions SelectedProvider">
                                            <SelectedProvider
                                                selectedProvider={selectedProvider}
                                            />
                                        </ErrorBoundary>
                                        <Table id="subscriptionDetailDiv" className='mt-3' borderless>
                                            <tbody>
                                                <tr>
                                                    <td style={{ width: '30%' }}>
                                                        <span id="subscription-plan" style={{ fontWeight: fontWeight.medium }}>Subscription Plan</span>
                                                    </td>
                                                    <td>
                                                        <span id="subscription-plan-value">{clientSubscriptionPlans[0].name}</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>
                                                        <span id="call-sessions" style={{ fontWeight: fontWeight.medium }}>Call Sessions</span>
                                                    </td>
                                                    <td className="d-flex">
                                                        <Progress className="successProgress my-auto mr-2" value={(100 * currentSubscription.serviceDetails.call.currentCycleUsage) / clientSubscriptionPlans[0].serviceDetails.call} /><span>{`${currentSubscription.serviceDetails.call.currentCycleUsage}/${clientSubscriptionPlans[0].serviceDetails.call}`}</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>
                                                        <span id="video-sessions" style={{ fontWeight: fontWeight.medium }}>Video Sessions</span>
                                                    </td>
                                                    <td className="d-flex">
                                                        <Progress className="successProgress my-auto mr-2" value={(100 * currentSubscription.serviceDetails.video.currentCycleUsage) / clientSubscriptionPlans[0].serviceDetails.video} /><span>{`${currentSubscription.serviceDetails.video.currentCycleUsage}/${clientSubscriptionPlans[0].serviceDetails.video}`}</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>
                                                        <span id="messaging" style={{ fontWeight: fontWeight.medium }}>Messaging</span>
                                                    </td>
                                                    <td className="d-flex">
                                                        <span id="unlimited-messaging">Unlimited</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>
                                                        <span id="start-date" style={{ fontWeight: fontWeight.medium }}>Start Date</span>
                                                    </td>
                                                    <td>
                                                        {/* baki */}
                                                        <span>{moment().subtract(3, 'days').format("MMM DD, dddd")}</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>
                                                        <span id="next-payment-date" style={{ fontWeight: fontWeight.medium }}>Next Payment Date</span>
                                                    </td>
                                                    <td>
                                                        {/* baki */}
                                                        <span>{currentSubscription.isCancelled ? "-" : moment().add(27, 'days').format("MMM DD, dddd")}</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>
                                                        <span id="days-passed" style={{ fontWeight: fontWeight.medium }}>Days Passed</span>
                                                    </td>
                                                    <td className="d-flex">
                                                        {/* baki */}
                                                        <Progress className="successProgress my-auto mr-2" value={(100 * 3) / 30} /><span>{`${3}/${30}`}</span>
                                                    </td>
                                                </tr>
                                                {/* <tr>
                                                    <td>
                                                        <span style={{ fontWeight: fontWeight.medium }}>Expires On</span>
                                                    </td>
                                                    <td>
                                                        <span>Unlimited</span>
                                                    </td>
                                                </tr> */}
                                            </tbody>
                                        </Table>
                                    </div>
                                    :
                                    availablePlans.length ?
                                        <div className="text-center">
                                            <div>
                                                <h1 style={{ fontFamily: "Montserrat, sans-serif", fontSize: "1.8rem", fontWeight: "500", color: "#384E73" }}>Subscriptions - Plans &amp; Pricings</h1>
                                                <h2 style={{ fontFamily: "Montserrat, sans-serif", fontSize: "1.2rem", fontWeight: "500" }}>Select subscription with your Counsellor that best suits your needs.<br />You can update / cancel your subscription anytime.</h2>
                                            </div>
                                            <div id="subscriptionPlanList">
                                                {
                                                    availablePlans.map((item, index) => (
                                                        <div key={index} className="d-inline-block col-8 col-sm-8 col-md-3 text-center px-2 py-3 m-3" style={selectedPlan.id == item.id ? theme.selectedSlotCard : theme.slotCard} onClick={() => setSelectedPlan(item)}>
                                                            <h2 style={Object.assign({ color: "#384E73" }, theme.subHeadingSemibold)}>{item.name}</h2>
                                                            <p>{`₹${item.amount / 100} / ${item.period}`}</p>
                                                            <div>
                                                                <p><i className={`fas fa-${item.call > 0 ? 'check' : 'times danger_text_color'} mr-2`}></i>{`${item.call} Call Sessions / ${item.period}`}</p>
                                                                <p><i className={`fas fa-${item.video > 0 ? 'check' : 'times danger_text_color'} mr-2`}></i>{`${item.video} Video Sessions / ${item.period}`}</p>
                                                                <p><i className={`fas fa-${item.report ? 'check' : 'times danger_text_color'} mr-2`}></i>{`Report Included`}</p>
                                                                <p><i className="fas fa-check mr-2"></i>{`Unlimited Messages`}</p>
                                                            </div>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                            <div>
                                                <button className="btn mx-auto mt-4" id="paymentBtn" type="button" style={theme.button} onClick={() => handlePlanSelect()}>
                                                    Continue Payment
                                                </button>
                                            </div>
                                        </div>
                                        :
                                        <div className="text-center">
                                            <h1 style={{ fontFamily: "Montserrat, sans-serif", fontSize: "1.8rem", fontWeight: "500", color: "#384E73" }}>Subscriptions - Plans &amp; Pricings</h1>
                                            {/* <h2 className='mt-5' style={{ fontFamily: "Montserrat, sans-serif", fontSize: "1.2rem", fontWeight: "500" }}>Selected counsellor does not have any subscription plans</h2> */}
                                            <div className="mt-4" style={{ backgroundColor: "#FFC107", color: "#FFFFFF", padding: "5px" }}>
                                                <span><i className="fas fa-exclamation-circle mr-1"></i>Selected counsellor does not have any subscription plans</span>
                                            </div>
                                        </div>
                                :
                                <div className="text-center">
                                    <div>
                                        <h1 style={{ fontFamily: "Montserrat, sans-serif", fontSize: "1.8rem", fontWeight: "500", color: "#384E73" }}>Subscriptions - Plans &amp; Pricings</h1>
                                        <h2 style={{ fontFamily: "Montserrat, sans-serif", fontSize: "1.2rem", fontWeight: "500" }}>Select subscription with your Counsellor that best suits your needs.<br />You can update / cancel your subscription anytime.</h2>
                                    </div>

                                    <div className="mt-4" style={{ backgroundColor: "#FFC107", color: "#FFFFFF", padding: "5px" }}>
                                        <span><i className="fas fa-exclamation-circle mr-1"></i>Counsellor not selected yet</span>
                                    </div>

                                    <div className="mt-4">
                                        <div className="d-flex">
                                            <img className="col-md-4 mx-auto" width="100%" height="100%" src={FindProviderImg} alt="Find Provider" />
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        <button className="btn" id="chooseProviderBtn" type="button" style={theme.button} onClick={() => props.history.replace('/counsellors')}>
                                            Choose Counsellor
                                        </button>
                                    </div>
                                </div>
                        }
                        {/* cancel sub
        survey for cancel sub
        update sub
        check why cancel */}
                    </div>
            }
        </div >
    )

}

export default Subscriptions;